import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  useRemoveAccountMutation,
  useRemoveBankMutation,
} from "@services/budget/mutations";
import { type IBudgetSettingsBank } from "@services/budget/types";

interface IRemoveBankModalProps extends IUseModalProps {
  bankId: number;
  bankAccountId?: number;
  budgetSettings: IBudgetSettingsBank;
}

export const RemoveBankModal: React.FC<IRemoveBankModalProps> = (props) => {
  const { bankAccountId, budgetSettings, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const accounts = budgetSettings.bankAccounts.filter((account) =>
    bankAccountId ? account.bankAccountId === bankAccountId : true,
  );

  const { mutateAsync: removeAccount, isPending: isRemoveAccountPending } =
    useRemoveAccountMutation();
  const { mutateAsync: removeBank, isPending: isRemoveBankPending } =
    useRemoveBankMutation();

  const isPending = bankAccountId
    ? isRemoveAccountPending
    : isRemoveBankPending;

  const handleSubmit = async () => {
    let response;

    // If provided bankAccountId, remove account, else remove whole bank
    if (bankAccountId) {
      response = await removeAccount({ bankAccountId }).catch(() => {
        toast.error(t("settings.budget.removeModal.toast.error"));
      });
    } else {
      response = await removeBank({ bankId: props.bankId }).catch(() => {
        toast.error(t("settings.budget.removeModal.toast.error"));
      });
    }

    if (response) {
      toast.success(t("settings.budget.removeModal.toast.success"));
    }

    modal.close();
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-xl"
      badgeProps={{ icon: BinIcon, color: "red" }}
      title={t("settings.budget.removeModal.title")}
      subtitle={
        <div>
          {accounts.map((account) => (
            <div key={account.bankAccountId}>
              {budgetSettings.bankName}: {account.iban}
            </div>
          ))}
        </div>
      }
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("settings.budget.removeModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit}
            isLoading={isPending}
          >
            {t("settings.budget.removeModal.confirm")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="px-4 pb-5 md:px-0 md:pb-0">
        <Notification
          color="red"
          description={t("settings.budget.removeModal.warning")}
        />
      </div>
    </Modal>
  );
};
